import React from "react";
import { useFormik } from "formik";

import CustomInput from "../formik/CustomInput.tsx";
import CustomTextArea from "../formik/CustomTextArea.tsx";
import CustomSelectField from "../formik/CustomSelectField.tsx";

import { INewPerception } from "../../interfaces/others/INewPerception.ts";

import {
  FrequencyList,
  PerceptionInitial,
} from "../../interfaces/formik/IPerception.ts";
import { PerceptionSchema } from "../../validation/schemas/PerceptionSchema.ts";
import { notification } from "../../utils/Notifications.tsx";
import Loader from "../Loader.tsx";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/index.ts";
import { saveIncentive, findIncentivesByPartner, updateIncentive, PerceptionTemplate } from "../../services/perceptions.service.ts";
import { setSelectedPartners } from "../../store/slices/payrollSlice.ts";
import TemplateAutocomplete from "./TemplateAutocomplete.tsx";

interface NewPerceptionProps extends INewPerception {
  onRefresh?: () => void;
  incentiveToEdit?: any;
  isEditing?: boolean;
}

const NewPerception: React.FC<NewPerceptionProps> = ({ close, onRefresh, incentiveToEdit, isEditing = false }) => {
  const dispatch = useDispatch();
  const { selectedPartners } = useSelector((state: RootState) => state.payroll);
  const [loading, setLoading] = React.useState(false);

  const formik = useFormik({
    initialValues: isEditing && incentiveToEdit ? {
      name: incentiveToEdit.name || "",
      description: incentiveToEdit.description || "",
      amount: incentiveToEdit.amount || 0,
      frequency: FrequencyList.find(item => item.name === incentiveToEdit.frequency)?.id || -99,
      startDate: incentiveToEdit.startDate || "",
      endDate: incentiveToEdit.endDate || "",
      applicationDate: incentiveToEdit.applicationDate || "",
      status: incentiveToEdit.status || "activo",
    } : PerceptionInitial,
    validationSchema: PerceptionSchema,
    onSubmit: async (values) => {
      setLoading(true);
      console.log('Form submitted with values:', values);

      try {
        if (isEditing) {
          // Editing mode - update single incentive
          if (!incentiveToEdit) {
            notification("Error ⚠️", "No se encontró el bono a editar", "danger");
            return;
          }

        const frequency = FrequencyList.find(
          (item) => item.id === values.frequency
        )?.name;

          const { applicationDate, startDate, endDate, ...data } = values;

          const incentiveData = {
            name: data.name,
            description: data.description,
            amount: Number(data.amount),
            frequency: frequency?.toLowerCase(),
            ...(applicationDate !== "" && { applicationDate }),
            ...(startDate !== "" && { startDate }),
            ...(endDate !== "" && values.frequency !== 4 && { endDate }), // No incluir endDate si es "único"
          };

          console.log('Updating incentive:', incentiveToEdit.id);
          console.log('Incentive data being sent:', incentiveData);
          const response = await updateIncentive(incentiveToEdit.id, incentiveData);
          console.log('Incentive updated:', response);
          
          notification("", "Bono actualizado exitosamente", "success");
        } else {
          // Creation mode - create for multiple partners
          console.log('Selected partners:', selectedPartners);

          if (!selectedPartners || selectedPartners.length === 0) {
            notification("Error ⚠️", "No hay colaboradores seleccionados", "danger");
            return;
          }

          // Validate required fields
          if (!values.name || !values.description || !values.amount || values.frequency === -99) {
            notification("Error ⚠️", "Complete todos los campos requeridos", "danger");
            return;
          }

          const frequency = FrequencyList.find(
            (item) => item.id === values.frequency
          )?.name;

          console.log('Frequency found:', frequency);

          const { applicationDate, startDate, endDate, ...data } = values;

          console.log('Data to send:', {
            ...data,
            frequency: frequency?.toLowerCase(),
            partnerIds: selectedPartners,
          });

          let createdCount = 0;
          const processedPartnerIds = new Set<number>();

          for (const partnerId of selectedPartners) {
            console.log('Processing partner ID:', partnerId);
            
            const incentiveData = {
              name: data.name,
              description: data.description,
              amount: Number(data.amount),
              frequency: frequency?.toLowerCase(),
              partnerId: partnerId,
              ...(applicationDate !== "" && { applicationDate }),
              ...(startDate !== "" && { startDate }),
              ...(endDate !== "" && values.frequency !== 4 && { endDate }), // No incluir endDate si es "único"
            };
            
            console.log('Sending incentive data:', incentiveData);
            
            try {
              console.log('Creating new incentive for partner ID:', partnerId);
              const response = await saveIncentive(incentiveData);
              console.log('Incentive created:', response);
              createdCount++;
              
              // Mark this partner as processed
              processedPartnerIds.add(partnerId);
            } catch (error) {
              console.error(`Error processing partner ${partnerId}:`, error);
              throw error; // Re-throw to be caught by the outer try-catch
            }
          }

          let message = "";
          if (createdCount > 0) {
            message = `${createdCount} bonos creados exitosamente`;
          } else {
            message = "Operación completada";
          }

          notification("", message, "success");
          dispatch(setSelectedPartners([]));
        }

        close();
        onRefresh?.(); // Call the refresh function if provided
      } catch (error) {
        console.error('Error saving incentives:', error);
        notification("Error ⚠️", "Error al guardar los bonos", "danger");
      } finally {
        setLoading(false);
      }
    },
  });

  React.useEffect(() => {
    if (formik.values.frequency === 4) {
      formik.setFieldValue('startDate', '');
      formik.setFieldValue('endDate', '');
    } else {
      formik.setFieldValue('applicationDate', '');
    }
  }, [formik.values.frequency]);

  const handleTemplateSelect = (template: PerceptionTemplate) => {
    formik.setFieldValue("name", template.name);
    formik.setFieldValue("description", template.description);
    formik.setFieldValue("amount", template.amount);
    notification("", `Plantilla "${template.name}" aplicada`, "success");
  };

  return (
    <React.Fragment>
      <Loader isLoading={loading} />
      <div>
        <CustomInput
          formik={formik}
          field="name"
          label="Nombre"
          placeholder="Nombre de la percepción"
          required
          sm={8}
        />
        
        {/* Autocompletado de plantillas - solo en modo creación */}
        {!isEditing && (
          <TemplateAutocomplete
            searchValue={formik.values.name}
            onTemplateSelect={handleTemplateSelect}
          />
        )}
      </div>
      <CustomTextArea
        field="description"
        formik={formik}
        label="Descripción"
        placeholder="Escribe alguna descripción"
        smLabel={3}
        sm={8}
        required
      />

      <div className="mt-3" />
      <CustomInput
        formik={formik}
        field="amount"
        label="Monto"
        type="number"
        placeholder="$100.00"
        required
        sm={8}
      />

      <CustomSelectField
        formik={formik}
        field="frequency"
        list={FrequencyList}
        label="Frecuencia"
        smLabel={3}
        sm={8}
        required
      />

      <div className="mt-3" />
          {formik.values.frequency !== 4 && ( // 4 = "único" en FrequencyList
            <CustomInput
              formik={formik}
              field="startDate"
              label="Fecha de inicio"
              type="date"
              required
              sm={8}
            />
          )}

      {formik.values.frequency === 4 && ( // 4 = "único" en FrequencyList
        <div className="mt-3">
          <CustomInput
            formik={formik}
            field="applicationDate"
            label="Fecha de aplicación"
            type="date"
            required
            sm={8}
          />
        </div>
      )}

      <div className="mt-3" />
          {formik.values.frequency !== 4 && ( // 4 = "único" en FrequencyList
            <CustomInput
              formik={formik}
              field="endDate"
              label="Fecha de fin"
              type="date"
              required
              sm={8}
            />
          )}

      {/* Fechas ocultas temporalmente */}

      <div className="d-flex justify-content-end">
        <button className="btn btn-secondary me-2" onClick={() => close()}>
          Cancelar
        </button>
        
        <button
          className="btn btn-success"
          onClick={() => formik.handleSubmit()}
        >
          {isEditing ? "Actualizar" : "Guardar"}
        </button>
      </div>
    </React.Fragment>
  );
};

export default NewPerception;
